import type { DataTypeVerbete } from "../hooks/sinalario";
import { normalizeSearchText } from "../utils/normalizeSearchText";

export type DictionaryCategoryId =
  | "family"
  | "animals"
  | "colors"
  | "food"
  | "body"
  | "emotions"
  | "time"
  | "school";

export type DictionaryCategory = {
  id: DictionaryCategoryId;
  label: string;
  icon: string;
  color: string;
  keywords: string[];
};

export const DICTIONARY_CATEGORIES: DictionaryCategory[] = [
  {
    id: "family",
    label: "Família",
    icon: "people",
    color: "#EC4899",
    keywords: [
      "família",
      "familia",
      "pai",
      "mãe",
      "mae",
      "filho",
      "filha",
      "irmão",
      "irmao",
      "irmã",
      "irma",
      "avô",
      "avo",
      "avó",
      "tio",
      "tia",
      "primo",
      "prima",
      "marido",
      "esposa",
      "family",
      "father",
      "mother",
      "child",
      "brother",
      "sister",
    ],
  },
  {
    id: "animals",
    label: "Animais",
    icon: "pet",
    color: "#F59E0B",
    keywords: [
      "animal",
      "cão",
      "cao",
      "gato",
      "cavalo",
      "pássaro",
      "passaro",
      "peixe",
      "vaca",
      "porco",
      "coelho",
      "leão",
      "leao",
      "dog",
      "cat",
      "bird",
      "fish",
    ],
  },
  {
    id: "colors",
    label: "Cores",
    icon: "colorfilter",
    color: "#8B5CF6",
    keywords: [
      "cor",
      "vermelho",
      "azul",
      "verde",
      "amarelo",
      "preto",
      "branco",
      "rosa",
      "roxo",
      "laranja",
      "color",
      "red",
      "blue",
      "green",
      "yellow",
      "black",
      "white",
    ],
  },
  {
    id: "food",
    label: "Comida",
    icon: "coffee",
    color: "#10B981",
    keywords: [
      "comida",
      "comer",
      "bebida",
      "beber",
      "fruta",
      "arroz",
      "feijão",
      "feijao",
      "pão",
      "pao",
      "leite",
      "café",
      "cafe",
      "food",
      "drink",
      "fruit",
      "bread",
    ],
  },
  {
    id: "body",
    label: "Corpo",
    icon: "hand",
    color: "#4614BB",
    keywords: [
      "corpo",
      "mão",
      "mao",
      "cabeça",
      "cabeca",
      "olho",
      "boca",
      "perna",
      "braço",
      "braco",
      "dedo",
      "coração",
      "coracao",
      "body",
      "hand",
      "head",
      "eye",
      "mouth",
    ],
  },
  {
    id: "emotions",
    label: "Sentimentos",
    icon: "heart",
    color: "#EF4444",
    keywords: [
      "sentimento",
      "feliz",
      "triste",
      "raiva",
      "medo",
      "amor",
      "alegre",
      "saudade",
      "emotion",
      "happy",
      "sad",
      "angry",
      "afraid",
      "love",
    ],
  },
  {
    id: "time",
    label: "Tempo",
    icon: "calendar",
    color: "#0EA5E9",
    keywords: [
      "tempo",
      "dia",
      "semana",
      "mês",
      "mes",
      "ano",
      "hora",
      "manhã",
      "manha",
      "tarde",
      "noite",
      "hoje",
      "amanhã",
      "amanha",
      "time",
      "day",
      "week",
      "month",
      "year",
    ],
  },
  {
    id: "school",
    label: "Escola",
    icon: "teacher",
    color: "#4614BB",
    keywords: [
      "escola",
      "professor",
      "aluno",
      "estudar",
      "aprender",
      "livro",
      "aula",
      "universidade",
      "school",
      "teacher",
      "student",
      "study",
      "learn",
      "book",
      "class",
    ],
  },
];

export function getDictionaryCategory(
  id: string
): DictionaryCategory | undefined {
  return DICTIONARY_CATEGORIES.find((category) => category.id === id);
}

export function isDictionaryCategoryId(
  value: string | null | undefined
): value is DictionaryCategoryId {
  if (!value) return false;
  return DICTIONARY_CATEGORIES.some((category) => category.id === value);
}

export function filterVerbetesByCategory(
  verbetes: DataTypeVerbete[],
  categoryId: DictionaryCategoryId
): DataTypeVerbete[] {
  const category = getDictionaryCategory(categoryId);
  if (!category) return [];

  const normalizedKeywords = category.keywords.map(normalizeSearchText);

  return verbetes
    .filter((verbete) => {
      if (!verbete?.palavra) return false;
      const haystack = normalizeSearchText(
        `${verbete.palavra} ${verbete.descricao || ""} ${
          verbete.categoria || ""
        }`
      );
      return normalizedKeywords.some((keyword) => haystack.includes(keyword));
    })
    .sort((a, b) =>
      normalizeSearchText(a.palavra).localeCompare(
        normalizeSearchText(b.palavra),
        "pt-BR"
      )
    );
}
